import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { ToolNavigationItem } from "../../models/navigation/ToolNavigationItem";
import { NavigationItem } from "./NavigationItem";
import { NavigationTopSection } from "./NavigationTopSection";
import { MobileToolLinks } from "./Tools/MobileToolLinks";

interface Props {
  isMobile: boolean;
  selectedTopLevel?: number | null;
  open?: boolean;
  color: string;
  navigationItems: NavigationItem[];
  mobileToolLinks: ToolNavigationItem[];
  onClickOutside?: (e?: React.MouseEvent | React.TouchEvent) => void;
  onClickInside?: (e?: React.MouseEvent | React.TouchEvent) => void;
}

let mainNavOriginalHeight = 0;

export const GroupNavigation = ({
  isMobile,
  selectedTopLevel,
  open = false,
  color,
  navigationItems,
  mobileToolLinks,
  onClickOutside,
  onClickInside
}: Props) => {
  const [overflowClass, setOverflowClass] = useState<string>("overflow-y-hidden");
  const [visibleClass, setVisibleClass] = useState<string>("invisible");
  const [navContainerHeight, updateNavContainerHeight] = useState<number>(0);

  const navRef = useRef<HTMLDivElement>(null);
  const navListRef = useRef<HTMLUListElement>(null);

  useBodyScrollLock(open);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    // Apply "overflow-y-auto" / "invisible" after transition is over
    // to avoid flickering

    if (open) {
      setVisibleClass("visible");
      timer = setTimeout(() => {
        setOverflowClass("overflow-y-auto");
      }, 200); // Match transition duration
    } else {
      setOverflowClass("overflow-y-hidden"); // Avoid scrollbar appearing when scrolling
      timer = setTimeout(() => {
        setVisibleClass("invisible");
      }, 200); // Match transition duration
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [open]);

  useEffect(() => {
    if (!navListRef?.current) {
      return;
    }

    const mainNavCurrentHeight = navListRef.current.getBoundingClientRect().height;

    if (mainNavCurrentHeight > 0) {
      mainNavOriginalHeight = mainNavCurrentHeight;
      updateNavContainerHeight(navListRef.current.clientHeight);
    }
  }, [selectedTopLevel, open]);

  const updateHeight = useCallback((height?: number) => {
    if (height === undefined) {
      updateNavContainerHeight(mainNavOriginalHeight);
      return;
    }

    updateNavContainerHeight(height);
  }, []);

  useEffect(() => {
    // Focus management
    if (!open || !navRef.current) {
      return;
    }

    const focusableElements = navRef.current.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements.length) {
      return;
    }

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab" && !e.shiftKey) {
        // Focus moves outside navigation
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }

      if (e.key === "Escape") {
        onClickOutside?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClickOutside, open]);

  return (
    <div
      ref={navRef}
      key="flyout-container"
      className={classNames(
        "flyout-container",
        open
          ? `h-screen flyout-container--open flyout-container--${color}`
          : "h-0 bg-white border-transparent pointer-events-none",
        overflowClass,
        visibleClass
      )}
      onClick={onClickOutside}
    >
      <nav
        key="nav-container"
        className="nav-container"
        style={{ height: !navContainerHeight ? "auto" : navContainerHeight }}
      >
        <div key="nav-inner" className="nav-inner" onClick={onClickInside}>
          <NavigationTopSection
            selectedTopLevel={!isMobile ? selectedTopLevel : null}
            isMobile={isMobile}
            items={navigationItems}
            color={color}
            open={open}
            ref={navListRef}
            updateContainerHeight={updateHeight}
          />
        </div>
      </nav>
      <div onClick={onClickInside}>
        <MobileToolLinks items={mobileToolLinks} />
      </div>
    </div>
  );
};

